import React from 'react';
import { formatMoney } from '../utils/money';
import { getCurrencyAccentBorder } from '../utils/currencyStyles';
import CurrencyBadge from './CurrencyBadge';
import { VISTA_CONSOLIDADO } from '../utils/currencyView';

const PylCurrencyBreakdown = ({
  desgloseMoneda = {},
  equivalenteArs,
  vista = VISTA_CONSOLIDADO,
  modoOscuro = false,
  textPri,
  textMuted,
  onSelectVista,
}) => {
  const entries = Object.entries(desgloseMoneda).filter(([, row]) => {
    const ing = Number(row.ingresos) || 0;
    const egr = Number(row.egresos) || 0;
    return ing !== 0 || egr !== 0;
  });
  if (!entries.length) return null;

  const totalArs = Math.abs(Number(equivalenteArs?.resultadoOperativo) || 0)
    || entries.reduce((sum, [, row]) => sum + Math.abs(Number(row.resultadoArs) || 0), 0);

  return (
    <div className="mb-3 space-y-2">
      <div>
        <p className={`text-[10px] font-semibold uppercase tracking-wide ${textMuted}`}>Cómo se arma por moneda</p>
        <p className={`text-[11px] mt-0.5 ${textMuted}`}>
          Cada card está en su divisa. El estado de resultados de arriba usa {vista === VISTA_CONSOLIDADO ? 'pesos convertidos con el TC de cada registro' : `solo ${vista}`}.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {entries.map(([moneda, row]) => {
          const resultado = Number(row.resultadoOperativo) || 0;
          const resultadoArs = Number(row.resultadoArs ?? row.equivalenteArs) || 0;
          const active = vista === moneda;
          const share = totalArs > 0 ? Math.min(100, Math.round((Math.abs(resultadoArs) / totalArs) * 100)) : 0;
          const resultColor = resultado >= 0
            ? (modoOscuro ? 'text-green-300' : 'text-green-700')
            : (modoOscuro ? 'text-red-300' : 'text-red-700');

          return (
            <button
              key={moneda}
              type="button"
              onClick={() => onSelectVista?.(moneda)}
              className={`rounded-xl border border-l-4 p-3 text-left transition-colors ${getCurrencyAccentBorder(moneda, modoOscuro)} ${
                active
                  ? modoOscuro ? 'bg-gray-800 border-gray-500 ring-1 ring-[#1F80FF]/50' : 'bg-white border-gray-300 ring-1 ring-[#1F80FF]/30'
                  : modoOscuro ? 'bg-gray-800/50 border-gray-700 hover:bg-gray-800' : 'bg-white border-gray-200 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-between gap-2 mb-2">
                <CurrencyBadge currency={moneda} modoOscuro={modoOscuro} />
                {moneda !== 'ARS' && resultadoArs !== 0 && (
                  <span className={`text-[10px] ${textMuted}`}>≈ {formatMoney(resultadoArs, 'ARS')}</span>
                )}
              </div>
              <div className="space-y-1">
                <div className="flex justify-between gap-2 text-[11px]">
                  <span className={textMuted}>Ingresos</span>
                  <span className={`font-semibold tabular-nums ${textPri}`}>
                    {formatMoney(row.ingresos, moneda, { showCode: moneda !== 'ARS' })}
                  </span>
                </div>
                <div className="flex justify-between gap-2 text-[11px]">
                  <span className={textMuted}>Egresos</span>
                  <span className={`font-semibold tabular-nums ${textPri}`}>
                    {formatMoney(row.egresos, moneda, { showCode: moneda !== 'ARS' })}
                  </span>
                </div>
                <div className="flex justify-between gap-2 text-[11px] pt-1">
                  <span className={`font-semibold ${textPri}`}>Resultado</span>
                  <span className={`font-semibold tabular-nums ${resultColor}`}>
                    {formatMoney(resultado, moneda, { showCode: moneda !== 'ARS' })}
                  </span>
                </div>
              </div>
              {vista === VISTA_CONSOLIDADO && share > 0 && (
                <div className="mt-2">
                  <div className={`h-1 rounded-full overflow-hidden ${modoOscuro ? 'bg-gray-700' : 'bg-gray-200'}`}>
                    <div className="h-full rounded-full bg-[#1F80FF]" style={{ width: `${share}%` }} />
                  </div>
                  <p className={`text-[10px] mt-1 ${textMuted}`}>{share}% del resultado en pesos</p>
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default PylCurrencyBreakdown;
